import { useEffect, lazy, Suspense } from 'react';
import './App.css';
import LandingPage from './LandingPage'
import Dashboard from './components/dashboard/Dashboard';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Apps from './components/Apps';
import LoanCalculator from './components/LoanCalculator';
import McpAccessPanel from './components/account/McpAccessPanel';
import McpSetupGuide from './components/account/McpSetupGuide';
import BlogPage from './components/blog/BlogPage';
import RouteAnalytics from './analytics/RouteAnalytics';
import PrivacyEasyFinances from './components/PrivacyEasyFinances';
import CvBuilderApp from './components/cv-builder/CvBuilderApp';
import GrowthPlannerApp from './components/growth-planner';
import LibroComprasApp from './components/compras/LibroComprasApp';
import LibroVentasApp from './components/ventas/LibroVentasApp';
import LibroGastosApp from './components/gastos/LibroGastosApp';
import PuntoEquilibrioApp from './components/punto-equilibrio/PuntoEquilibrioApp';
import { FreeAppLayout, PremiumAppLayout } from './components/AppLayout';
import GoToDashboardButton from './components/goToDashboard';
import DashboardApp from './components/tools-dashboard/DashboardApp';
import InventarioApp from './components/inventario/InventarioApp';
import { useAuthStore } from './store/authStore';
import Login from './components/Login';
import GoogleAuthCallback from './components/GoogleAuthCallback';
import GoogleAccountPanel from './components/account/GoogleAccountPanel';
import { cloudSync } from './utils/cloudStorage';
import { DashboardPropietario } from './components/citas/DashboardPropietario';
import { ReservaCliente } from './components/citas/ReservaCliente';
import FastFoodApp from './components/fast_food/FastFoodApp';
import AppsAccessDashboard from './components/kadskljasdljkdas/AppsAccessDashboard';
import RequireAuth, { ReturnAfterLogin } from './components/RequireAuth';
// import QuotationBot from './components/QuotationBot';
import FitnessApp from './components/fitness';
import RetailPOSApp from './components/retail-pos/RetailPOSApp';
import DevssoftChatApp from './components/devssoft-chat/DevssoftChatApp';
import CoreBaseApp from './components/corebase/CoreBaseApp';
import ProjectBaseApp from './components/projectbase/ProjectBaseApp';
import CatalogPublico from './components/catalog/CatalogPublico';
import OpenToolsPage from './landing/OpenToolsPage';
import NotFoundPage from './landing/NotFoundPage';

const FormsApp = lazy(() => import('./components/forms/FormsApp'));
const PersonalFinanceApp = lazy(() => import('./components/personal-finance/PersonalFinanceApp'));
const FormsRespond = lazy(() => import('./components/forms/FormsRespond'));

function App() {
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    const init = async () => {
      await cloudSync.pull();
    };
    if (isAuthenticated) init();
  }, [isAuthenticated]);

  return (
    <BrowserRouter>
      <RouteAnalytics />
      {/* Definición de las rutas */}
      <Routes>
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPage />} />
        <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
        <Route path="/account/google" element={<RequireAuth><GoogleAccountPanel /></RequireAuth>} />
        <Route path="/account/mcp-guide" element={<RequireAuth><McpSetupGuide /></RequireAuth>} />
        <Route path="/account/mcp-access" element={<RequireAuth><McpAccessPanel /></RequireAuth>} />
        <Route
          path="/kadskljasdljkdas/apps-access"
          element={
            <RequireAuth>
              <AppsAccessDashboard />
            </RequireAuth>
          }
        />

        <Route path="/login" element={!isAuthenticated ? <FreeAppLayout
            title="Login - DevsSoft"
            description="Accede a una variedad de herramientas para tus actividades diarias."
            keywords=""
            showCloudSyncButton={false}
          ><Login /></FreeAppLayout> : <ReturnAfterLogin />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/herramientas-abiertas" element={<OpenToolsPage />} />
        <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
        <Route path="/apps" element={<Apps />} />
        <Route path="/forms/:formId" element={<Suspense fallback={<p>Cargando encuesta…</p>}><FormsRespond /></Suspense>} />
        <Route path="/reservar/:ownerId" element={
          <FreeAppLayout
            title="Mis Citas - DevsSoft"
            description="Solicita una cita en tu negocio favorito."
            keywords="Citas, reservas, agenda, clientes"
            showCloudSyncButton={false}
          >
            <ReservaCliente />
          </FreeAppLayout>
        } />
        <Route element={<RequireAuth />}>
          <Route path="/apps/personal-finances" element={<FreeAppLayout mobileApp title="Finanzas personales - DevsSoft" description="Registra tu dinero, aprende a presupuestar y entiende tus finanzas. Todos los reportes incluidos sin suscripción." showCloudSyncButton={false}><Suspense fallback={<p>Cargando finanzas personales…</p>}><PersonalFinanceApp /></Suspense></FreeAppLayout>} />
          <Route path="/apps/forms" element={<FreeAppLayout title="Devssoft Forms" description="Crea encuestas, recibe respuestas y encuentra lo que importa." showCloudSyncButton={false}><Suspense fallback={<p>Cargando Forms…</p>}><FormsApp /></Suspense></FreeAppLayout>} />
          <Route path="/apps/loan-calculator" element={<LoanCalculator />} />
          <Route path="/apps/cv-builder" element={<FreeAppLayout
            title="Constructor de Currículum - DevsSoft"
            description="Crea y gestiona múltiples perfiles profesionales y currículums."
            keywords="curriculum, cv, resume, perfil profesional"
            showCloudSyncButton={false}
          >
            <CvBuilderApp />
          </FreeAppLayout>} />
          <Route path="/apps/growth-planner" element={<FreeAppLayout
            title="Growth Planner - DevsSoft"
            description="Planifica tu crecimiento profesional, rutas de carrera y progreso."
            keywords="carrera, crecimiento profesional, planificación, skills, certificaciones"
          >
            <GrowthPlannerApp />
          </FreeAppLayout>} />
          <Route path="/apps/citas" element={<FreeAppLayout
            title="Citas - DevsSoft"
            description="Gestiona citas con clientes en tu negocio."
            keywords="Citas, reservas, agenda, clientes"
            showCloudSyncButton={false}
          >
            <DashboardPropietario />
          </FreeAppLayout>} />
          <Route path="/apps/inventario" element={<PremiumAppLayout
            title="Inventario - DevsSoft"
            description="Controla las entradas, salidas, y saldos de tu almacén."
            keywords="inventario, kardex, entradas, salidas, stock"
          >
            <InventarioApp />
          </PremiumAppLayout>} />
          <Route
            path="/apps/fastfood"
            element={
              <PremiumAppLayout
                title="POS - DevsSoft"
                description="Sistema POS para comida rápida"
                keywords="pos comida rapida, restaurante, ventas"
              >
                <FastFoodApp />
              </PremiumAppLayout>
            }
          />
          <Route path="/apps/tools-dashboard" element={<FreeAppLayout
            title="Tools Dashboard - DevsSoft"
            description="Controla los ingresos, costos, y gastos operativos de tu negocio."
            keywords="libro de ingresos, gastos, costos, finanzas"
          >
            <DashboardApp />
          </FreeAppLayout>} />
          <Route path="/apps/compras" element={<FreeAppLayout
            title="Libro de Compras - DevsSoft"
            description="Registra, controla y analiza tus compras del negocio."
            keywords="libro de compras, contabilidad, control de gastos"
          >
            <GoToDashboardButton />
            <LibroComprasApp />
          </FreeAppLayout>} />
          <Route path="/apps/gastos" element={<FreeAppLayout
            title="Libro de Gastos - DevsSoft"
            description="Registra y controla los gastos operativos de tu negocio."
            keywords="libro de gastos, costos, finanzas"
          >
            <GoToDashboardButton />
            <LibroGastosApp />
          </FreeAppLayout>} />
          <Route path="/apps/fitness" element={<FreeAppLayout
            title="Fitness - DevsSoft"
            description="Registra y controla tus actividades físicas."
            keywords="fitness, ejercicio, salud"
          >
            <FitnessApp />
          </FreeAppLayout>} />
          <Route path="/apps/ventas" element={<FreeAppLayout
            title="Libro de Ventas - DevsSoft"
            description="Controla tus ventas y analiza tus ingresos."
            keywords="libro de ventas, ingresos, negocios"
          >
            <GoToDashboardButton />
            <LibroVentasApp />
          </FreeAppLayout>} />
          <Route path="/apps/punto-equilibrio" element={<FreeAppLayout
            title="Punto de Equilibrio - DevsSoft"
            description="Calcula el punto de equilibrio de tu negocio fácilmente."
            keywords="punto de equilibrio, costos fijos, costos variables"
          >
            <PuntoEquilibrioApp />
          </FreeAppLayout>} />
          <Route
            path="/apps/retail-pos"
            element={
              <PremiumAppLayout
                title="POS Retail Ropa - DevsSoft"
                description="Sistema de inventario y punto de venta para tiendas de ropa con código de barras."
                keywords="pos, punto de venta, inventario, ropa, código de barras, retail"
              >
                <RetailPOSApp />
              </PremiumAppLayout>
            }
          />
          <Route
            path="/apps/devssoft-chat"
            element={
              <PremiumAppLayout
                title="DevsSoft Chat - DevsSoft"
                description="Consulta y analiza tus negocios con IA empresarial."
                keywords="chat, ia, analisis, inventario, pos, retail"
              >
                <DevssoftChatApp />
              </PremiumAppLayout>
            }
          />
          <Route
            path="/apps/corebase"
            element={
              <PremiumAppLayout
                title="CoreBase CRM - DevsSoft"
                contentMaxWidth={1600}
                description="CRM para negocios de servicios. Gestiona leads, ventas y seguimiento comercial."
                keywords="crm, leads, ventas, seguimiento, clientes, servicios"
              >
                <CoreBaseApp />
              </PremiumAppLayout>
            }
          />
          <Route path="/apps/projectbase" element={<PremiumAppLayout title="ProjectBase PM - DevsSoft" description="Planifica proyectos, recursos y resultados con gestión nativa desde MCP." keywords="proyectos, recursos, cursos, talleres, gestión" showCloudSyncButton={false}><ProjectBaseApp /></PremiumAppLayout>} />
        </Route>
        <Route path="/privacy/easyFinances" element={<PrivacyEasyFinances />} />
        {/* Catálogo público (sin auth) */}
        <Route path="/store/:slug" element={<CatalogPublico />} />
        <Route path="/store/:slug/p/:productId" element={<CatalogPublico />} />
        {/* <Route path="/cotizador" element={<QuotationBot />} /> */}
        {/* Ruta para manejar errores 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
